import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, combineLatest, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    shareReplay,
    skip,
    startWith,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { CommonModule } from '@angular/common';
import { flatten, isEqual, uniqWith } from 'lodash-es';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';

import { ShAutocompleteComponent } from '../../../form-controls/components/_core/autocomplete/autocomplete.component';
import { ShIndeterminateDirective } from '../../../form-controls/directives/indeterminate.directive';
import { DropdownDirection as DroppanelOpenDirection } from '../../../form-controls/entities/dropdown-direction.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { MultiFormSelectOptions } from '../../../form-controls/entities/multi-form-select-options.entity';
import { LoadingBehaviorSubject } from '../../../helpers/loading-behavior-subject.helper';
import { ClearMarginModifiers } from '../../entities/clear-margin-modifier.entity';
import { ArrowNavigationHelper } from '../../helpers/arrow-navigation.helper';
import { ShActionItemComponent } from '../action-item/action-item.component';
import { ActionItem, ActionItemModifier, ActionItemStyle } from '../action-item/entities/action-item.entity';
import { ShDroppanelComponent } from '../droppanel/droppanel.component';
import { DroppanelStyle } from '../droppanel/entities/droppanel-style.entity';
import { IconSize } from '../icon/entities/icon-sizes.entity';
import { ShIconModule } from '../icon/icon.module';
import { ShChildIndexDirective } from '../../directives/child-index/child-index';

@Component({
    standalone: true,
    selector: 'sh-droppanel-select',
    templateUrl: './droppanel-select.component.html',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        ShActionItemComponent,
        ShIconModule,
        ShDroppanelComponent,
        ShIndeterminateDirective,
        ShAutocompleteComponent,
        ShChildIndexDirective
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShDroppanelSelectComponent<T = string>
    extends BaseContainer
    implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit
{
    private readonly OPTION_QUERY: string = '.sh-droppanel__list-action:not(.-sh-disabled)';
    /* Base sh-droppanel properties */
    public readonly limitHeightToViewport$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    /* Selection properties */
    private selectedState$!: Observable<FormValue<FormSelectOption<T>>[]>;

    readonly initialSelectedValues$: ReplaySubject<FormValue<T>[]> = new ReplaySubject<FormValue<T>[]>();
    readonly state$: BehaviorSubject<FormValue<FormSelectOption<T>>[]> = new BehaviorSubject<
        FormValue<FormSelectOption<T>>[]
    >([]);

    public selectedValues$!: Observable<T[]>;
    public showExtraItemsLabelIndicator$!: Observable<boolean>;

    /* Search properties */
    private searchTerm$!: Observable<string>;
    public searchControl: UntypedFormControl;

    /* Autocomplete properties */
    public initialAutocompleteOptions$: Subject<MultiFormSelectOptions<T>> = new Subject<MultiFormSelectOptions<T>>();
    public selectedSuggestions$: BehaviorSubject<FormSelectOption<T>[]> = new BehaviorSubject<FormSelectOption<T>[]>(
        []
    );

    public autocompleteInputControl: UntypedFormControl;
    public autocomplete = false;

    /* Options */
    private readonly multiFormSelectOptions$: ReplaySubject<MultiFormSelectOptions<T>> = new ReplaySubject<
        MultiFormSelectOptions<T>
    >(1);

    public filteredOptions$!: Observable<FormSelectOption<T>[]>;
    public cappedFilteredOptions$!: Observable<FormSelectOption<T>[]>;
    public hasOptions$!: Observable<boolean>;

    /* Delete */
    readonly deletePress$: Subject<KeyboardEvent> = new Subject<KeyboardEvent>();

    private readonly DELETE_INPUT_KEYS: string[] = ['Backspace', 'Delete', 'Clear'];

    /* Others */
    public sizes: typeof IconSize = IconSize;
    public states: typeof FormValueState = FormValueState;
    public clearMarginModifiers: ClearMarginModifiers = ClearMarginModifiers.CLEAR_MARGIN_TOP_IMPORTANT;
    private readonly extraItemCount$: BehaviorSubject<number> = new BehaviorSubject(0);

    // Injectors
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    private arrowNavigation: ArrowNavigationHelper;

    @HostListener('window:scroll', ['$event'])
    onWindowScroll(event: Event) {
        this.setDropdownClosed();
    }

    @HostListener('keydown', ['$event'])
    handleKeyDown(event: KeyboardEvent) {
        // To prevent interfering with other components
        this.arrowNavigation.handleKeyDown({
            event,
            optionContainerElement: this.optionContainerElement,
            toggleContainer: this.toggleContainer,
            searchElement: this.searchElement,
            closeDropdown: () => this.setDropdownClosed()
        });
    }

    /* Messages */
    @Input() noResultsFoundMsg: string = this.translateService.translate('ui.no-results');
    @Input() loadingMsg = this.translateService.translate('ui.loading');
    @Input() searchPlaceholderMsg: string = this.translateService.translate('ui.search');
    @Input() listCappedMsg = this.translateService.translate('ui.load-more');

    /* Base sh-droppanel inputs */
    @Input() inOverflownContainer = false;
    @Input() height = 300;
    @Input() openDirection: DroppanelOpenDirection = DroppanelOpenDirection.Below;
    @Input() droppanelStyle: DroppanelStyle = DroppanelStyle.FollowsParentWidth;

    @Input() isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    @Input() loading$: LoadingBehaviorSubject = new LoadingBehaviorSubject(false);
    @Input() set limitHeightToViewport(limitHeightToViewport: boolean) {
        this.limitHeightToViewport$.next(limitHeightToViewport);
    }

    /* Specific sh-droppanel-select inputs */
    @Input() id?: string = 'droppanel';
    @Input() search = false;
    @Input() multiple = false;
    @Input() allowSingleDeselect = true;
    @Input() itemCapCount = 50;
    @Input() noResultsActionItem?: ActionItem;
    @Input() sortAlphabetically = false;
    @Input() autocompleteFct?: (term: string) => Observable<string[]>;
    @Input() valueToLabelFct?: (value: T) => string;

    /* Available options and initial selected values */
    @Input() set options(value: MultiFormSelectOptions<T>) {
        if (value) {
            this.multiFormSelectOptions$.next(value);
        }
    }

    @Input() set initialSelectedValues(values: FormValue<T>[]) {
        if (values) {
            this.initialSelectedValues$.next(values);
        }
    }

    @Output() searchTerm: EventEmitter<string> = new EventEmitter<string>();
    @Output() shownItemCount: EventEmitter<number> = new EventEmitter<number>();
    @Output() selectionState: EventEmitter<FormValue<FormSelectOption<T>>[]> = new EventEmitter<
        FormValue<FormSelectOption<T>>[]
    >();

    @ViewChild('toggleContainer') toggleContainer: ElementRef<HTMLElement>;
    @ViewChild('searchInput') searchElement?: ElementRef<HTMLElement>;
    @ViewChild('optionContainer') optionContainerElement?: ElementRef<HTMLElement>;

    constructor(private readonly formBuilder: UntypedFormBuilder) {
        super();
    }

    ngOnInit(): void {
        this.initArrowNavigation();

        this.initControls();
        this.initSourceStreams();
        this.initStateStreams();

        this.initListeners();
        this.initHasOptionsStream();
        this.autocomplete = !!this.autocompleteFct;
    }

    ngAfterViewInit(): void {
        this.initStateChangeListener();
    }

    ngAfterViewChecked() {
        this.arrowNavigation.manageFocus(this.isOpen$.value, this.optionContainerElement, this.searchElement);
    }

    ngOnDestroy(): void {
        super.ngOnDestroy();

        this.limitHeightToViewport$.complete();

        this.multiFormSelectOptions$.complete();
        this.state$.complete();

        this.initialAutocompleteOptions$.complete();
        this.selectedSuggestions$.complete();

        this.deletePress$.complete();
    }

    private initArrowNavigation(): void {
        this.arrowNavigation = new ArrowNavigationHelper(
            this.cdr,
            this.isOpen$,
            this.destroy$,
            this.OPTION_QUERY,
            true
        );
    }

    private initControls(): void {
        this.searchControl = this.formBuilder.control('');
        this.autocompleteInputControl = this.formBuilder.control('');
    }

    // #region initSourceStreams
    private initSourceStreams(): void {
        this.searchTerm$ = this.buildSearchTermStream();
        this.selectedState$ = this.buildSelectedStateStream();
        this.filteredOptions$ = this.buildFilteredOptionsStream();
        this.cappedFilteredOptions$ = this.buildCappedFilteredOptionsStream();
    }

    private buildSearchTermStream(): Observable<string> {
        if (this.search) {
            return (this.searchControl.valueChanges as Observable<string>).pipe(
                debounceTime(300),
                distinctUntilChanged(isEqual),
                tap((searchTerm: string) => this.searchTerm.emit(searchTerm)),
                startWith('')
            );
        } else {
            return of('');
        }
    }

    private buildSelectedStateStream(): Observable<FormValue<FormSelectOption<T>>[]> {
        return this.state$.pipe(
            map(formValues => formValues.filter(formValue => formValue.state !== FormValueState.UNSET))
        );
    }

    private buildFilteredOptionsStream(): Observable<FormSelectOption<T>[]> {
        // Don't show the selected items when a search term is entered
        const searchDependentState$: Observable<FormValue<FormSelectOption<T>>[]> = combineLatest([
            this.selectedState$,
            this.searchTerm$
        ]).pipe(
            map(([selectedState, searchTerm]) => {
                if (searchTerm.length > 0) {
                    return [];
                } else {
                    return selectedState;
                }
            })
        );

        // Merge the selected items with the stream of options
        const allSelectOptions$ = merge(this.multiFormSelectOptions$, this.initialAutocompleteOptions$);
        const mergedOptions$: Observable<MultiFormSelectOptions<T>> = combineLatest([
            searchDependentState$,
            allSelectOptions$
        ]).pipe(
            map(([searchSelectedState, multiFormSelectOptions]) => {
                const options: FormSelectOption<T>[] = multiFormSelectOptions.options;
                const unselected: FormSelectOption<T>[] = options.filter(
                    option => !searchSelectedState.find(el => isEqual(el.value.value, option.value))
                );

                const selected: FormSelectOption<T>[] = searchSelectedState.map(state => state.value);

                if (this.sortAlphabetically) {
                    selected.sort((a, b) => a.label.localeCompare(b.label));
                    unselected.sort((a, b) => a.label.localeCompare(b.label));
                }

                const mergedOptions: FormSelectOption<T>[] = [...selected, ...unselected];
                return { isLiveData: multiFormSelectOptions.isLiveData, options: mergedOptions };
            })
        );

        /* Only emit new options when the dropdown is closed or the options are updated */
        const throttledOptionRefresh$: Observable<MultiFormSelectOptions<T>> = combineLatest([
            this.isOpen$,
            this.multiFormSelectOptions$
        ]).pipe(
            map(([isOpen]) => isOpen),
            filter(isOpen => isOpen !== false),
            withLatestFrom(mergedOptions$),
            map(([_, mergedOptions]) => mergedOptions)
        );

        return combineLatest([this.searchTerm$, throttledOptionRefresh$]).pipe(
            map(([searchTerm, multiFormSelectOptions]: [string, MultiFormSelectOptions<T>]) => {
                const options: FormSelectOption<T>[] = multiFormSelectOptions.options;
                if (multiFormSelectOptions.isLiveData) {
                    return [...options];
                } else {
                    return [...this.filterCurrentSelectOptions(searchTerm, options)];
                }
            }),
            shareReplay(1)
        );
    }

    private filterCurrentSelectOptions(searchTerm: string, options: FormSelectOption<T>[]): FormSelectOption<T>[] {
        return options.filter((option: FormSelectOption<T>) =>
            option.label.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }

    private buildCappedFilteredOptionsStream(): Observable<FormSelectOption<T>[]> {
        return combineLatest([this.filteredOptions$, this.selectedSuggestions$, this.extraItemCount$]).pipe(
            map(([filteredOptions, selectedSuggestions, extraItemCount]) => {
                return this.search || this.autocomplete
                    ? this.combinedFilteredSelectedSuggestion(filteredOptions, selectedSuggestions).slice(
                          0,
                          this.itemCapCount + extraItemCount
                      )
                    : filteredOptions;
            })
        );
    }

    private combinedFilteredSelectedSuggestion(
        filteredOptions: FormSelectOption<T>[],
        selectedSuggestions: FormSelectOption<T>[]
    ): FormSelectOption<T>[] {
        // method ensures that suggestions added are not duplicated on closing and reopening SINGLE multiselect drop panel
        if (selectedSuggestions.length === 0) {
            return filteredOptions;
        }

        return uniqWith([...filteredOptions, ...selectedSuggestions], isEqual);
    }
    // #endregion

    private initStateStreams(): void {
        this.selectedValues$ = this.state$.pipe(
            map(input => input.filter(formValue => formValue.state === FormValueState.SET)),
            map(input => input.map(formValue => formValue.value.value))
        );
        this.showExtraItemsLabelIndicator$ = combineLatest([this.filteredOptions$, this.cappedFilteredOptions$]).pipe(
            map(([filteredOptions, cappedFilteredOptions]) => {
                return (this.search || this.autocomplete) && filteredOptions.length > cappedFilteredOptions.length;
            })
        );
    }

    private initListeners(): void {
        this.initInputListener();
        this.initDeletePressListener();
        this.initOptionsListener();
        this.initSelectedSuggestionsListener();
        this.initInitialAutocompleteOptionsListener();
        this.initExtraCountListener();
        this.initOpenListener();
    }

    private initExtraCountListener(): void {
        this.extraItemCount$.pipe(takeUntil(this.destroy$)).subscribe(extraItemCount => {
            this.shownItemCount.next(extraItemCount + this.itemCapCount);
        });
    }

    // #region initListeners
    private initInputListener(): void {
        this.initialSelectedValues$
            .pipe(
                withLatestFrom(this.multiFormSelectOptions$.pipe(startWith({ isLiveData: true, options: [] }))),
                takeUntil(this.destroy$)
            )
            .subscribe(([formValues, multiFormSelectOptions]) => {
                this.setState(this.setOptionSelected(formValues, multiFormSelectOptions));
            });

        // Once the options are loaded, check if any labels needs updating
        this.multiFormSelectOptions$
            .pipe(takeUntil(this.destroy$))
            .subscribe(options => this.updateStateWithOptions(options.options));
    }

    private updateStateWithOptions(options: FormSelectOption<T>[]): void {
        this.state$.pipe(take(1), takeUntil(this.destroy$)).subscribe(state => {
            const newState: FormValue<FormSelectOption<T>>[] = state.map(formValue => {
                if (formValue.value.value !== '') {
                    return formValue;
                }

                const formSelectOption: FormSelectOption<T> | undefined = options.find(option =>
                    isEqual(option.value, formValue.value.value)
                );
                if (formSelectOption) {
                    return new FormValue(formSelectOption, formValue.state);
                }

                return formValue;
            });

            this.setState(newState);
        });
    }

    private setOptionSelected(
        formValues: FormValue<T>[],
        multiFormSelectOptions: MultiFormSelectOptions<T>
    ): FormValue<FormSelectOption<T>>[] {
        // Base selection, based on the available values in multiFormSelectOptions
        const options: FormValue<FormSelectOption<T>>[] = multiFormSelectOptions.options.map(formSelectOption => {
            const value: T = formSelectOption.value;

            const formValue: FormValue<T> | undefined = formValues.find(fv => isEqual(fv.value, value));

            const state: FormValueState = formValue ? formValue.state : FormValueState.UNSET;
            return new FormValue(formSelectOption, state);
        });

        /* When multiFormSelectOptions are live, the contents do not represent all selectable values,
        but can be dependent on e.g. search query, pagination,...
            Allow selection of any random value. It is up to the wrapper code to make sure it's a valid value.
         */
        if (multiFormSelectOptions.isLiveData === true) {
            const extraSelected: FormValue<T>[] = formValues.filter(formvalue =>
                options.every(option => !isEqual(option.value.value, formvalue.value))
            );

            const extraState: FormValue<FormSelectOption<T>>[] = extraSelected.map(
                formValue =>
                    new FormValue(
                        { label: this.estimateLabelOfMissingValue(formValue.value), value: formValue.value },
                        formValue.state
                    )
            );
            options.push(...extraState);
        }

        return options;
    }

    // Estimate label for values that are not present in provided options
    // Functions can be manually provided via valueToLabelFct to translate objects
    private estimateLabelOfMissingValue(value: T): string {
        if (typeof value === 'string') {
            return value;
        } else if (!!this.valueToLabelFct) {
            return this.valueToLabelFct(value);
        }

        return '';
    }

    private initStateChangeListener(): void {
        this.state$.pipe(takeUntil(this.destroy$)).subscribe(state => this.selectionState.emit(state));
    }

    private initDeletePressListener(): void {
        this.deletePress$
            .pipe(
                withLatestFrom(this.state$),
                map(([_, state]) => state),
                filter(state => state.length > 0),
                takeUntil(this.destroy$)
            )
            .subscribe(() => {
                this.unsetAllOptions();
            });
    }

    private initOpenListener(): void {
        this.isOpen$.pipe().subscribe(isOpen => {
            if (isOpen) {
                this.arrowNavigation.resetFocus();
                this.activate();
            } else if (this.isActiveComponent()) {
                this.deactivate();
            }
        });
    }

    private initOptionsListener(): void {
        this.multiFormSelectOptions$
            .pipe(distinctUntilChanged(isEqual), withLatestFrom(this.state$), takeUntil(this.destroy$))
            .subscribe(([options, state]) => {
                const newState: FormValue<FormSelectOption<T>>[] = state;
                let modified = false;

                options.options.forEach((option: FormSelectOption<T>) => {
                    const index: number = newState.findIndex((state: FormValue<FormSelectOption<T>>) =>
                        isEqual(state.value.value, option.value)
                    );

                    if (index > -1) {
                        newState[index] = new FormValue(option, newState[index].state);
                        modified = true;
                    }
                });

                if (modified) {
                    this.setState(newState);
                }
            });
    }

    private initInitialAutocompleteOptionsListener(): void {
        this.initialAutocompleteOptions$.pipe(take(1), takeUntil(this.destroy$)).subscribe();
    }

    private initSelectedSuggestionsListener(): void {
        this.selectedSuggestions$
            .pipe(skip(1), withLatestFrom(this.state$), takeUntil(this.destroy$))
            .subscribe(([selectedSuggestions, state]: [FormSelectOption<T>[], FormValue<FormSelectOption<T>>[]]) => {
                if (this.multiple) {
                    const updatedState: FormValue<FormSelectOption<T>>[] = this.toggleSelectedSuggestions(
                        selectedSuggestions,
                        state
                    );

                    this.setState(updatedState);
                } else {
                    this.setState(selectedSuggestions.map(suggestion => new FormValue(suggestion)));
                }
            });
    }

    private toggleSelectedSuggestions(
        selectedSuggestions: FormSelectOption<T>[],
        state: FormValue<FormSelectOption<T>>[]
    ): FormValue<FormSelectOption<T>>[] {
        // Toggle state of existing options or add new option to state
        const newState: FormValue<FormSelectOption<T>>[][] = selectedSuggestions.map(
            (suggestionOption: FormSelectOption<T>) => {
                const isInCurrentState = !!state.find((stateEntry: FormValue<FormSelectOption<T>>) =>
                    isEqual(stateEntry.value.value, suggestionOption.value)
                );

                if (isInCurrentState) {
                    return state.map((stateEntry: FormValue<FormSelectOption<T>>) => {
                        const toggledState: FormValueState =
                            stateEntry.state === FormValueState.SET ? FormValueState.UNSET : FormValueState.SET;

                        return isEqual(stateEntry.value.value, suggestionOption.value)
                            ? new FormValue(suggestionOption, toggledState)
                            : stateEntry;
                    });
                } else {
                    return [...state, new FormValue(suggestionOption)];
                }
            }
        );

        const newStateFlat: FormValue<FormSelectOption<T>>[] = flatten(newState);

        // Remove possible duplicates
        return uniqWith(newStateFlat, isEqual);
    }

    // #endregion

    private initHasOptionsStream(): void {
        this.hasOptions$ = this.cappedFilteredOptions$.pipe(
            take(1),
            map(() => true)
        );
    }

    // #region Search
    private resetSearch(): void {
        if (this.search) {
            this.searchControl.reset('');
        }
    }
    // #endregion

    // #region Autocomplete

    public receiveInitialAutocompleteOptions(autocompleteOptions: MultiFormSelectOptions<T>): void {
        this.initialAutocompleteOptions$.next(autocompleteOptions);
    }

    public receiveSelectedSuggestion(suggestions: FormSelectOption<T>[]): void {
        if (suggestions.length <= 0) {
            return;
        }

        this.selectedSuggestions$.next(suggestions);

        this.autocompleteInputControl.setValue('');
    }
    // #endregion

    // #region State
    private setState(newState: FormValue<FormSelectOption<T>>[]): void {
        this.state$.next(newState);
    }

    public getState(formSelectOption: FormSelectOption<T>): FormValueState {
        const formValue: FormValue<FormSelectOption<T>> | undefined = this.state$.value.find(fv =>
            isEqual(fv.value.value, formSelectOption.value)
        );

        if (typeof formValue === 'undefined') {
            return FormValueState.UNSET;
        }
        return formValue.state;
    }

    public isIndeterminate(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.INDETERMINATE;
    }

    public isUnset(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.UNSET;
    }

    public isSet(formSelectOption: FormSelectOption<T>): boolean {
        return this.getState(formSelectOption) === FormValueState.SET;
    }

    // #endregion

    // #region Option helpers
    private unsetAllOptions(): void {
        const newState: FormValue<FormSelectOption<T>>[] = this.state$.value.map(
            formValue => new FormValue(formValue.value, FormValueState.UNSET)
        );
        this.setState(newState);
    }

    public toggleCheckbox(event: Event, selectedOption: FormSelectOption<T>): void {
        let target: HTMLElement | null = event.target as HTMLElement | null;
        if (target && target?.tagName !== 'INPUT') {
            target = target.querySelector('input');
            (target as HTMLInputElement).checked = !(target as HTMLInputElement).checked;
        }
        const isChecked: boolean = (target as HTMLInputElement).checked;

        if (isChecked && selectedOption.disabled) {
            return;
        }

        const state: FormValueState = isChecked ? FormValueState.SET : FormValueState.UNSET;

        const currentValue: FormValue<FormSelectOption<T>>[] = [...this.state$.value];
        const index: number = currentValue.findIndex(fv => isEqual(fv.value.value, selectedOption.value));

        if (index > -1) {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(currentValue[index].value, state);
            currentValue.splice(index, 1, formValue);
        } else {
            const formValue: FormValue<FormSelectOption<T>> = new FormValue(selectedOption, FormValueState.SET);
            currentValue.push(formValue);
        }

        this.resetSearch();

        this.setState(currentValue);
    }

    public selectSingleValue(selectedOption: FormSelectOption<T>): void {
        if (selectedOption.disabled) {
            return;
        }

        this.resetSearch();

        if (this.isSet(selectedOption) && this.allowSingleDeselect) {
            this.unsetAllOptions();
            this.setDropdownClosed();
            return;
        }

        this.setState([new FormValue(selectedOption)]);
        this.setDropdownClosed();
    }
    // #endregion

    // #region Helpers
    public setDropdownClosed(): void {
        this.isOpen$.next(false);
    }

    public handleDeletePress(keyboardEvent: KeyboardEvent): void {
        if (this.DELETE_INPUT_KEYS.includes(keyboardEvent.key)) {
            this.deletePress$.next(keyboardEvent);
        }
    }

    public trackByFn(index: number, option: FormSelectOption<T>): T {
        return option.value;
    }

    public isDisabled(option: FormSelectOption, state: FormValueState): boolean {
        return !!option.disabled && (!option.allowUnsetDisabled || state !== this.states.SET);
    }

    private increaseExtraItems() {
        this.extraItemCount$.next(this.extraItemCount$.value + Math.max(10, this.itemCapCount / 2));
    }

    public get loadMoreActionItem(): ActionItem {
        return {
            label: this.listCappedMsg,
            style: ActionItemStyle.Link,
            modifiers: [ActionItemModifier.Bold],
            eventListener: () => this.increaseExtraItems()
        };
    }

    public onSearchFocus(): void {
        this.arrowNavigation.onSearchFocus();
    }

    public get focusIndex(): number {
        return this.arrowNavigation?.focusIndex ?? -1;
    }

    public onHover(event: MouseEvent): void {
        this.arrowNavigation.onHover(event.target as HTMLElement, this.optionContainerElement);
    }

    public onHoverSearch(): void {
        this.arrowNavigation.setSearchFocusIndex(-1);
    }
    // #endregion
}
